import React from 'react';
import { useTournament } from '../hooks/useTournament';

const TeamSettings = () => {
  const { numTeams, updateTeams, players, teamAssignmentHistory, matchSchedule,
    manualTeamSizes, getEffectiveTeamSizes, canAdjustPairSize, adjustPairSize, resetManualPair } = useTournament();

  const handleIncrease = () => {
    if (numTeams < Math.min(players.length, 50)) {
      // Show confirmation if there's existing data
      if ((teamAssignmentHistory.length > 0 || matchSchedule.length > 0) && 
          !window.confirm('Wenn Sie die Anzahl der Teams ändern, werden alle Zuweisungen neu generiert und aktuelle Daten gehen verloren. Fortfahren?')) {
        return;
      }
      updateTeams(numTeams + 1);
    }
  };

  const handleDecrease = () => {
    if (numTeams > 2) {
      // Show confirmation if there's existing data
      if ((teamAssignmentHistory.length > 0 || matchSchedule.length > 0) && 
          !window.confirm('Wenn Sie die Anzahl der Teams ändern, werden alle Zuweisungen neu generiert und aktuelle Daten gehen verloren. Fortfahren?')) {
        return;
      }
      updateTeams(numTeams - 1);
    }
  };

  const effectiveSizes = getEffectiveTeamSizes();

  return (
    <div className="section">
      <div className="section-header">
        <h2>Team-Einstellungen</h2>
      </div>
      <div className="number-input-container">
        <div className="number-input-label">
          <i className="icon team-icon">👥</i>
          <span>Anzahl der Teams:</span>
        </div>
        <div className="number-input-control">
          <button 
            className="number-control-button"
            onClick={handleDecrease}
            disabled={numTeams <= 2 || players.length < 2}
          >
            −
          </button>
          <span className="number-display">{numTeams}</span>
          <button 
            className="number-control-button"
            onClick={handleIncrease}
            disabled={numTeams >= Math.min(players.length, 50) || players.length < 2}
          >
            +
          </button>
        </div>
      </div>
      
      {players.length > 0 && numTeams > 0 && (() => {
        const minSize = Math.min(...effectiveSizes);
        const maxSize = Math.max(...effectiveSizes);
        const hasManual = manualTeamSizes.some(v => v != null);
        const text = minSize === maxSize
          ? `Jedes Team hat ${minSize} Spieler`
          : `Aktuelle Teamgrößen: ${minSize}–${maxSize} Spieler`;
        return (
          <div className="info-text">
            {text}{hasManual ? ' (manuelle Verteilung aktiv)' : ''}
          </div>
        );
      })()}
      
      {/* Team assignment table: shows each team and assigned player count */}
      {players.length >= 2 && numTeams > 0 && (
        <table className="tournament-table team-assignment-table">
          <thead>
            <tr>
              <th>Team</th>
              <th>Zugewiesene Spieler</th>
              <th>Spielerzahl manuell</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.ceil(numTeams / 2) }).map((_, pairIdx) => {
              const a = pairIdx * 2;
              const b = a + 1;
              const labelA = String.fromCharCode(65 + a);
              const labelB = b < numTeams ? String.fromCharCode(65 + b) : null;
              const isManual = (manualTeamSizes[a] != null) || (labelB && manualTeamSizes[b] != null);
              return (
                <tr key={pairIdx} className={isManual ? 'manual-active-row' : ''}>
                  <td>{labelB ? `${labelA}/${labelB}` : `${labelA}`}</td>
                  <td>{labelB ? `${effectiveSizes[a]} / ${effectiveSizes[b]}` : `${effectiveSizes[a]}`}</td>
                  <td>
                    <div className="manual-controls">
                      <button
                        className="btn-mini"
                        title="−1 für beide"
                        disabled={!canAdjustPairSize(a, -1)}
                        onClick={() => adjustPairSize(a, -1)}
                      >−</button>
                      <span className="manual-count">{labelB ? `${effectiveSizes[a]} / ${effectiveSizes[b]}` : `${effectiveSizes[a]}`}</span>
                      <button
                        className="btn-mini"
                        title="+1 für beide"
                        disabled={!canAdjustPairSize(a, +1)}
                        onClick={() => adjustPairSize(a, +1)}
                      >+</button>
                      <button
                        className="btn-mini btn-reset"
                        title="Manuelle Anpassung zurücksetzen (Paar)"
                        onClick={() => resetManualPair(a)}
                      >⟳</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {players.length >= 2 && numTeams > 0 && (
        <p className="info-text">Anpassungen gelten für Paare und halten beide Teamgrößen gleich (A/B, C/D, E/F, …). Manuell angepasste Paare sind hervorgehoben.</p>
      )}
      
      {players.length < 2 && (
        <p className="error-message">Fügen Sie mindestens 2 Spieler hinzu, um Teams zu erstellen</p>
      )}
    </div>
  );
};

export default TeamSettings;